/**
 * A URNA — bolas claras e escuras dentro de um pote, e as que o aluno acrescentou.
 *
 * Uma urna torna visível o que a fração esconde: quando se acrescentam bolas de uma cor,
 * o numerador cresce E o denominador cresce junto. Na conta isso é uma linha; no desenho,
 * é o pote enchendo.
 *
 * A PROBABILIDADE NUNCA APARECE. O seletor acrescenta bolas e o desenho muda, mas nenhum
 * número diz a chance — senão bastaria mover o seletor até o número bater, e o item
 * mediria paciência em vez de proporção. É a mesma regra da prancha que não inclina
 * (`render/gangorra.ts`).
 *
 * AS BOLAS FICAM AGRUPADAS por tipo, e não embaralhadas. Uma urna de verdade é mexida,
 * mas aqui a figura é um instrumento de CONTAGEM: agrupadas, as quantidades se leem de
 * relance; espalhadas, o aluno gastaria a atenção contando em vez de pensando. E as
 * acrescentadas vêm por último, em destaque, para o gesto dele ficar visível.
 */

import {
  adjetivo,
  capitalizar,
  conta,
  umA,
  verbo,
  type AdjetivoId,
  type NomeId,
} from '../core/pt.ts';
import { scene, type Scene, type Shape } from './scene.ts';

/** Lado do quadrado que cada bola ocupa, em unidades de desenho. */
const U = 26;
const R = 10.5;
/** Bolas por fileira. Com 7, vinte e uma bolas cabem em três fileiras. */
const COLS = 7;

export interface OpcoesUrna {
  /** Bolas claras (as que não são o evento). */
  readonly claras: number;
  /** Bolas escuras já na urna. */
  readonly escuras: number;
  /** Escuras acrescentadas pelo aluno; entram em destaque, depois das outras. */
  readonly acrescentadas?: number;
  /**
   * Para quantas bolas o pote é desenhado. É o que mantém o pote do MESMO tamanho
   * enquanto o aluno acrescenta: ele começa parcialmente cheio e enche, em vez de
   * crescer junto com as bolas — o que faria a figura pular a cada volta do seletor.
   */
  readonly reserva?: number;
  readonly title: string;
  readonly desc: string;
}

export function urna(o: OpcoesUrna): Scene {
  const acrescentadas = o.acrescentadas ?? 0;
  const total = o.claras + o.escuras + acrescentadas;
  const linhas = Math.max(1, Math.ceil(Math.max(total, o.reserva ?? total) / COLS));

  const larg = COLS * U;
  const alt = linhas * U;
  const folga = 0.42 * U;
  /** Quanto cada parede se fecha da boca até o fundo: dá ao pote cara de pote. */
  const incl = 0.5 * U;

  const shapes: Shape[] = [];

  // O pote: duas paredes e um fundo, aberto em cima. Desenhado ANTES das bolas, para
  // que uma bola encostada na parede fique por cima do traço, e não por baixo.
  const x0 = -folga;
  const x1 = larg + folga;
  const yBoca = alt + folga * 1.6;
  shapes.push({
    t: 'poly',
    pts: [
      { x: x0 - incl, y: yBoca },
      { x: x0, y: 0 },
      { x: x1, y: 0 },
      { x: x1 + incl, y: yBoca },
    ],
    closed: false,
    role: 'given',
  });
  // A boca, marcada por dois traços curtos: sem eles o pote parece um funil cortado.
  shapes.push({
    t: 'seg',
    a: { x: x0 - incl - 0.26 * U, y: yBoca },
    b: { x: x0 - incl, y: yBoca },
    role: 'given',
  });
  shapes.push({
    t: 'seg',
    a: { x: x1 + incl, y: yBoca },
    b: { x: x1 + incl + 0.26 * U, y: yBoca },
    role: 'given',
  });

  // As bolas, de baixo para cima e da esquerda para a direita: claras, escuras, e por
  // fim as acrescentadas.
  const centro = (i: number): { x: number; y: number } => {
    const linha = Math.floor(i / COLS);
    const naLinha = Math.min(COLS, total - linha * COLS);
    // Fileira incompleta fica centrada, como um punhado de bolas se acomodaria.
    const sobra = (COLS - naLinha) * U * 0.5;
    return { x: sobra + (i % COLS) * U + U / 2, y: linha * U + U / 2 };
  };

  for (let i = 0; i < total; i++) {
    const c = centro(i);
    const escura = i >= o.claras;
    const nova = i >= o.claras + o.escuras;
    shapes.push({
      t: 'circle',
      c,
      r: R,
      role: nova ? 'result' : 'given',
      fill: escura && !nova ? 'ink' : nova ? 'result-fill' : 'surface',
    });
  }

  // O enquadramento acompanha o pote, que é fixo pela reserva — nunca as bolas.
  return scene(shapes, {
    viewBox: {
      x: x0 - incl - 0.6 * U,
      y: -0.5 * U,
      w: larg + 2 * folga + 2 * incl + 1.2 * U,
      h: yBoca + 0.9 * U,
    },
    a11y: { title: o.title, desc: o.desc },
  });
}

/**
 * As palavras do contexto, para a descrição falar do mesmo objeto que o enunciado.
 *
 * Esta função dizia "Uma urna com 8 bolas claras e 2 bolas escuras" **sempre** — e o
 * enunciado podia estar falando de fichas lisas e pintadas numa caixa de gincana. Quem
 * usa leitor de tela recebia a única descrição da figura contradizendo o texto que
 * acabara de ouvir. As três formas de plural também eram escritas à mão aqui.
 */
export interface PalavrasDaUrna {
  readonly recipiente: NomeId;
  readonly peca: NomeId;
  readonly clara: AdjetivoId;
  readonly escura: AdjetivoId;
}

export function describeUrna(
  w: PalavrasDaUrna,
  claras: number,
  escuras: number,
  acrescentadas = 0,
): string {
  const cheias = escuras + acrescentadas;
  const postas = conta(acrescentadas, w.peca);
  return (
    `${capitalizar(String(umA(w.recipiente)))} com ${conta(claras, w.peca).adj(w.clara)} e ` +
    `${conta(cheias, w.peca).adj(w.escura)}` +
    (acrescentadas > 0
      ? `, das quais ${acrescentadas} ${verbo('ser.pret', postas.n)} ${adjetivo('acrescentado', postas.g, postas.n)} por você.`
      : '.')
  );
}
